import React from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '../../../components/Seo';
import { CimmplePayMarkdown, splitLeadingH1 } from '../../../components/docs/CimmplePayMarkdown';
import { cimmplePayDocsOverviewMarkdown } from '../../../content/cimmplepay/overview.md';

const docCards = [
  {
    title: 'Getting Started',
    to: '/docs/cimmplepay/getting-started',
    description: 'Environments, API conventions, and your first successful call.',
  },
  {
    title: 'Authentication',
    to: '/docs/cimmplepay/authentication',
    description: 'JWT login, bearer tokens, company switching, and common auth errors.',
  },
  {
    title: 'Core Flows',
    to: '/docs/cimmplepay/core-flows',
    description: 'Recommended partner workflows for employees, payroll, outputs, and journals.',
  },
  {
    title: 'API Quick Reference',
    to: '/docs/cimmplepay/api-quick-reference',
    description: 'High-value endpoints to implement an ERP integration quickly.',
  },
  {
    title: 'Go-Live Checklist',
    to: '/docs/cimmplepay/go-live-checklist',
    description: 'Security, mapping, functional validation, and operational readiness.',
  },
] as const;

const CimmplePayDocsHome: React.FC = () => {
  const { title, body } = splitLeadingH1(cimmplePayDocsOverviewMarkdown);

  return (
    <div className="pt-24">
      <Seo
        title="CimmplePay Docs | Cimmple"
        description="Practical integration documentation for partners connecting ERP systems to CimmplePay: authentication, core flows, API quick reference, and go-live checklist."
      />

      <section className="pt-8 pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-3 text-balance">
            {title ?? 'CimmplePay Docs'}
          </h1>
          <p className="text-lg text-slate-700 dark:text-dark-300 max-w-3xl mx-auto text-balance">
            Integration guides for external ERP and implementation partners: authenticate safely, sync payroll data, retrieve outputs, and (optionally) sync accounting journals.
          </p>
        </div>
      </section>

      <section className="pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {docCards.map((card) => (
              <Link key={card.to} to={card.to} className="card-glow block h-full">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-2">
                      {card.title}
                    </h2>
                    <p className="text-slate-700 dark:text-dark-300">{card.description}</p>
                  </div>
                  <span className="text-primary-600 dark:text-primary-400 font-semibold whitespace-nowrap">
                    Open
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card p-8 md:p-12 prose prose-slate dark:prose-invert max-w-none">
            <CimmplePayMarkdown markdown={body} />
          </div>
        </div>
      </section>
    </div>
  );
};

export default CimmplePayDocsHome;
